import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  limit,
  type QueryConstraint,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { converters } from "@/lib/firestore";
import { useAuth } from "@/hooks/useAuth";
import type { Studio } from "@/types/firestore";

export interface StudioWithId extends Studio {
  id: string;
}

const saveApiKeyFn = httpsCallable(functions, "saveStudioApiKey");
const testConnectionFn = httpsCallable<
  { apiKey?: string; studioId?: string },
  { success: boolean; error?: string }
>(functions, "testStudioConnection");
const syncAgentsFn = httpsCallable<
  { studioId: string },
  { success: boolean; count?: number; error?: string }
>(functions, "syncStudioAgents");

interface StudiosState {
  studioKey: string | null;
  studios: StudioWithId[];
  loading: boolean;
  error: string | null;
}

function initialState(studioKey: string | null): StudiosState {
  return { studioKey, studios: [], loading: studioKey !== null, error: null };
}

export function useStudios(orgId?: string) {
  const { user, role } = useAuth();
  // useAuth exposes a synthetic ORG_OWNER while a platform user impersonates
  // an organization. The effective role must control aggregate eligibility.
  const hasPlatformScope = role === "PLATFORM_OWNER" || role === "PLATFORM_ADMIN";
  const studioKey = orgId ? `org:${orgId}` : hasPlatformScope ? "platform" : null;
  const [state, setState] = useState<StudiosState>(() => initialState(studioKey));

  useEffect(() => {
    let cancelled = false;
    setState(initialState(studioKey));

    if (!studioKey) {
      return;
    }

    const ref = collection(db, "studios").withConverter(converters.studios);
    const constraints: QueryConstraint[] = [];
    if (orgId) {
      constraints.push(where("orgId", "==", orgId));
    }
    // Only add orderBy when not filtering by orgId to avoid needing a composite index.
    // DataTable handles client-side sorting.
    if (!orgId) {
      constraints.push(orderBy("name", "asc"));
    }
    constraints.push(limit(200));
    const q = query(ref, ...constraints);

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        if (cancelled) return;
        setState({
          studioKey,
          studios: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          loading: false,
          error: null,
        });
      },
      (err) => {
        if (cancelled) return;
        console.error("Error fetching studios:", err);
        setState({
          studioKey,
          studios: [],
          loading: false,
          error: "Failed to load studios.",
        });
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [studioKey, orgId]);

  const createStudio = useCallback(
    async (data: {
      orgId: string;
      name: string;
      pickaxeStudioId: string | null;
      workspaceUrl: string | null;
      studioUrl: string;
      loginUrl?: string | null;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const ref = doc(collection(db, "studios"));
      await setDoc(ref, {
        orgId: data.orgId,
        name: data.name,
        pickaxeStudioId: data.pickaxeStudioId ?? null,
        workspaceUrl: data.workspaceUrl ?? null,
        studioUrl: data.studioUrl,
        loginUrl: data.loginUrl ?? null,
        pickaxeApiKey: "",
        status: "active",
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        lastSyncAt: null,
        historyVisibleToOrg: false,
        documentsVisibleToOrg: false,
      });
      return ref.id;
    },
    [user]
  );

  const updateStudio = useCallback(
    async (studioId: string, data: Partial<Pick<Studio, "name" | "status" | "loginUrl" | "studioUrl" | "pickaxeStudioId" | "workspaceUrl" | "historyVisibleToOrg" | "documentsVisibleToOrg" | "tosResourceId" | "tosResourceName" | "tosLabel">>) => {
      const ref = doc(db, "studios", studioId);
      await updateDoc(ref, data);
    },
    []
  );

  const deleteStudio = useCallback(async (studioId: string) => {
    const ref = doc(db, "studios", studioId);
    await deleteDoc(ref);
  }, []);

  const saveApiKey = useCallback(
    async (studioId: string, apiKey: string) => {
      await saveApiKeyFn({ studioId, apiKey });
    },
    []
  );

  const testConnection = useCallback(
    async (params: { apiKey?: string; studioId?: string }): Promise<{ success: boolean; error?: string }> => {
      try {
        const result = await testConnectionFn(params);
        return result.data;
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : "Connection test failed";
        return { success: false, error: message };
      }
    },
    []
  );

  const syncAgents = useCallback(
    async (studioId: string) => {
      const result = await syncAgentsFn({ studioId });
      return result.data;
    },
    []
  );

  const isCurrent = state.studioKey === studioKey;
  return {
    studios: isCurrent ? state.studios : [],
    loading: isCurrent ? state.loading : studioKey !== null,
    error: isCurrent ? state.error : null,
    createStudio,
    updateStudio,
    deleteStudio,
    saveApiKey,
    testConnection,
    syncAgents,
  };
}
